import React, { useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { TextField, Chip } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import PropTypes from 'prop-types';

McpCatalogMultiSelectField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
    })).isRequired
};

/**
 * Multi-select field rendered as an MUI Autocomplete (multiple) whose
 * selected values are shown as chips inside the input.
 *
 * The form value is stored as an array of option ids (string[]).
 */
function McpCatalogMultiSelectField({ name, label, required, options }) {
    const { control, errors, trigger, setValue, getValues } = useFormContext();

    // When there is only one option available, select it by default (only if the field is still empty)
    useEffect(() => {
        if (options.length === 1) {
            const currentValue = getValues(name);
            const isEmpty = !Array.isArray(currentValue) || currentValue.length === 0;
            if (isEmpty) {
                setValue(name, [options[0].id]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);

    let isError = false;
    let errorMessage = '';
    if (errors && errors.hasOwnProperty(name)) {
        isError = true;
        errorMessage = errors[name].message;
    }

    const handleBlur = () => {
        trigger(name);
    };

    return (
        <div className="form-addTicket">
            <label className="form-addTicket-label">
                {label}
                {required && <span className="required">*</span>}
            </label>
            <div className="form-addTicket-input mcp-catalog-multiselect-input">
                <Controller
                    name={name}
                    control={control}
                    render={({ value, onChange }) => {
                        // value is an array of ids; map to option objects for Autocomplete
                        const selectedIds = Array.isArray(value) ? value : [];
                        const selectedOptions = selectedIds
                            .map(id => options.find(opt => opt.id === id))
                            .filter(Boolean);

                        // Hide already-selected items from the dropdown; they reappear once deselected
                        const availableOptions = options.filter(opt => !selectedIds.includes(opt.id));

                        return (
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                disableClearable
                                options={availableOptions}
                                classes={{ option: 'mcp-catalog-option' }}
                                getOptionLabel={option => option.label || ''}
                                value={selectedOptions}
                                getOptionSelected={(option, selectedValue) =>
                                    option.id === selectedValue?.id
                                }
                                onBlur={handleBlur}
                                onChange={(e, newValue) => {
                                    onChange(newValue.map(opt => opt.id));
                                }}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option.id}
                                            label={option.label}
                                            className="mcp-catalog-os-chip"
                                        />
                                    ))
                                }
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        className="form-control-input form-control-wrapInput"
                                        error={isError}
                                        helperText={errorMessage}
                                    />
                                )}
                                renderOption={option => option.label}
                                ListboxProps={{
                                    style: { maxHeight: 200, overflow: 'auto' }
                                }}
                            />
                        );
                    }}
                />
            </div>
        </div>
    );
}

McpCatalogMultiSelectField.defaultProps = {
    required: false
};

export default McpCatalogMultiSelectField;
